import {
    Component, ElementRef,
    forwardRef,
    HostListener,
    inject,
    Injector,
    input,
    OnDestroy,
    viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validator, ValidatorFn } from '@angular/forms';
import { DateModelPipe } from 'app/shared/date/date-model.pipe';
import { DateModel, dateModelToString, stringToDateModel } from 'app/shared/date/date.model';
import { OverlayRef, OverlaysService, POSITION_TARGET, PositionTarget } from 'app/shared/ui/overlays';
import { CalendarComponent } from './calendar.component';


const getYear = (year: string) => {
    if (!year) {
        return new Date().getFullYear();
    }
    if (year.length == 2) {
        const firstTwoNumsOfCurrentYear = new Date().getFullYear().toString().slice(0, 2);
        return +`${firstTwoNumsOfCurrentYear + year}`;
    }
    if (year.length == 1) {
        const firstThreeNumsOfCurrentYear = new Date().getFullYear().toString().slice(0, 3);
        return +`${firstThreeNumsOfCurrentYear + year}`;
    }
    return +year;
};

const isDateValid = (date: string | null) => {
    if (date === null) {
        return true;
    }
    if (typeof date !== 'string') {
        return;
    }

    const dateValues = date.split(/[ ./-]+/, 3);
    const newMonth = +dateValues[0] - 1;
    const newDay = +dateValues[1];

    const insertedYear = dateValues[2];
    const newDate = new Date();
    newDate.setFullYear(getYear(insertedYear), newMonth, newDay);

    const dateIsValid = newDate.getMonth() === newMonth
    && newDate.getDate() === newDay
    && newDate.getFullYear() === getYear(insertedYear);

    return dateIsValid;
};

const dateValidator: ValidatorFn = c => isDateValid(c.value) ? null : { 'invalid-date': true };



@Component({
    selector: 'ui-date-picker',
    imports: [
        ReactiveFormsModule,
    ],
    styles: [
        `
        :host {
            position: relative;
            display: block;
        }

        .calendar-control:not(:disabled) {
            background: white;
        }
        `,
    ],
    providers: [
        DateModelPipe,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true,
        },
    ],
    template: `
        <span class="input-group">
            <input
                #input
                class="form-control calendar-control"
                [class.is-invalid]="formControl.invalid"
                (blur)="onBlur()"
                [placeholder]="placeholder()"
                [formControl]="formControl">
            <span class="input-group-append">
                <button
                    type=button
                    class="input-group-text"
                    (click)="toggleCalendar()">
                    <i class="fa fa-calendar"></i>
                </button>
            </span>
        </span>
    `,
})
export class DatePickerComponent implements ControlValueAccessor, Validator,PositionTarget, OnDestroy {

    inputElement = viewChild.required<ElementRef>('input');

    placeholder = input('Type a date...');

    formControl = new FormControl('', { validators: dateValidator, updateOn: 'blur' });

    private _date: DateModel | null;

    private calendarRef: OverlayRef | null;

    private onChange = (_: any) => {};

    private onTouched = () => {};
    
    private dateModelPipe = inject(DateModelPipe);

    get date() {
        return this._date;
    }

    set date(value: DateModel | null) {
        this._date = value;
        this.formControl.setValue(value ? this.dateModelPipe.transform(value, 'MM/dd/yyyy') : null);
    }

    private elementRef = inject(ElementRef);
    
    private overlaysService = inject(OverlaysService);
    
    private injector = inject(Injector);

    constructor() {
        this.formControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
            if (value === '') {
                this.date = null;
            }
            let dateModel: DateModel | null = null;
            if (value !== null) {
                dateModel = stringToDateModel(value);
            }
            this.onChange(dateModel ? dateModelToString(dateModel) : null);
        });
    }

    validate(_c: AbstractControl) {
        return this.formControl.errors;
    }

    /**
     * handler for [for] directive
     */
    @HostListener('click')
    onClick() {
        this.inputElement().nativeElement.click();
    }

    onBlur() {
        if (this.formControl.valid) {
            this.writeValue(this.formControl.value);
            this.onChange(this.date ? dateModelToString(this.date) : null);
        }
    }

    toggleCalendar() {
        if (!this.calendarRef) {
            // prevent document:click on calendar to be called and calendar immediately closed
            setTimeout(() => {
                this.openCalendar();
            });
        } else {
            this.closeCalendar();
        }
    }

    writeValue(value: string | null) {
        this.date = value ? stringToDateModel(value) : null;
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.closeCalendar();
        }
        this.formControl[isDisabled ? 'disable' : 'enable']();
    }

    openCalendar() {
        if (this.formControl.disabled) {
            return;
        }
        const injector = Injector.create({
            providers: [{ provide: POSITION_TARGET, useValue: this }],
            parent: this.injector,
        });
        this.calendarRef = this.overlaysService.createOverlay(CalendarComponent, {
            inputs: { date: this.date },
            parentInjector: injector,
        });
        this.calendarRef.componentRef.onDestroy(() => {
            this.calendarRef = null;
        });
        this.calendarRef.result.subscribe(date => {
            this.date = date;
            this.onChange(this.date ? dateModelToString(this.date) : null);
            this.onTouched();
            this.inputElement().nativeElement.focus();
        });
    }

    getBoundingClientRect() {
        return this.elementRef.nativeElement.getBoundingClientRect();
    }

    closeCalendar() {
        if (this.calendarRef) {
            this.calendarRef.dismiss();
        }
    }

    @HostListener('keydown.tab')
    @HostListener('keydown.shift.tab')
    tabPressed() {
        this.closeCalendar();
    }

    ngOnDestroy() {
        this.closeCalendar();
    }
}
